// React imports
import React, { useRef } from "react";

// MUI imports
import Icon from "@mui/material/Icon";
import Radio from "@mui/material/Radio";
import {Autocomplete, TextField, IconButton, Chip, Grid, Skeleton, Alert} from "@mui/material";

// MUI Icons imports
import StarIcon from "@mui/icons-material/Star";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';

// MUI Date Picker imports
import { enGB } from "date-fns/locale";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

// Customized MUI components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

// Other imports
import PropTypes from "prop-types";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Tooltip from "@mui/material/Tooltip";
import DOMPurify from "dompurify";
import { getOfferWindow, getHSSWindow, calculateHssSurcharge } from "./hardcodedData";
import { formatAmount } from "./TravelOrderUtility";


// Visual identity for the "why" behind a discount — kept distinct from the
// "% OFF" pill (the "what") so users can learn to scan for urgency at a glance.
const OFFER_TAG_STYLES = {
    early_bird: { label: "Early Bird", color: "#7B1FA2", bg: "#F3E5F5", icon: "alarm" },
    last_minute: { label: "Last Minute", color: "#C62828", bg: "#FDECEA", icon: "bolt" },
    group_deal: { label: "Group Deal", color: "#616161", bg: "#EEEEEE", icon: "groups" },
};

const HSS_TAG_STYLE = { label: "High Season Surcharge", color: "#B45309", bg: "#FEF3C7", icon: "trending_up" };

const SelectTripStepSkeleton = () => (
    <MDBox px={3}>
        <Skeleton variant="text" width="20%" height={50} />
        <Grid container spacing={3}>
            <Grid item xs={50} md={5}>
                <Skeleton variant="text" width="100%" height={70} />
            </Grid>
            <Grid item xs={50} md={3.5}>
                <Skeleton variant="text" width="100%" height={70} />
            </Grid>
            <Grid item xs={50} md={3.5}>
                <Skeleton variant="text" width="100%" height={70} />
            </Grid>
        </Grid>
        <Grid container spacing={3}>
            <Grid item xs={50} md={3}>
                <Skeleton variant="text" width="100%" height={200} />
            </Grid>
            <Grid item xs={50} md={3}>
                <Skeleton variant="text" width="100%" height={200} />
            </Grid>
            <Grid item xs={50} md={3}>
                <Skeleton variant="text" width="100%" height={200} />
            </Grid>
            <Grid item xs={50} md={3}>
                <Skeleton variant="text" width="100%" height={200} />
            </Grid>
        </Grid>
        <Skeleton variant="text" width="30%" height={50} />
        <Skeleton variant="text" width="100%" height={200} />
        <Skeleton variant="text" width="30%" height={50} />
        <Skeleton variant="text" width="100%" height={200} />
        <Skeleton variant="text" width="40%" height={50} />
        <Skeleton variant="text" width="100%" height={50} />
    </MDBox>
);


function SelectTripStep({
    isLoadingTripPackages,
    isLoadingOrderForEdit,
    filteredTripPackages,
    // Filter related params
    searchDeparture,
    onSearchDepartureChange,
    travelAfterDate,
    onTravelAfterDateChange,
    travelBeforeDate,
    onTravelBeforeDateChange,
    // Trip Package params
    selectedTripPackage,
    onSelectTripPackage,
    ClearPackageSelection,
    // Departure Date params
    selectedDepartureDate,
    allocationMap,
    onChangeDepartureDate,
    isLoadingAllocations,
    hssTemplateMap = {},
    fieldErrors = {},
}) {

    // Returns message when there is no trips to show due to some filter

    const getNoTripPackageMessage = () => {
        const hasSearch = searchDeparture.trim() !== "";
        const hasAfter = travelAfterDate != null;
        const hasBefore = travelBeforeDate != null;

        let noTripsMessage = "No trips found.";

        if (hasSearch && hasAfter && hasBefore) {
            noTripsMessage =
                "No trips match your search and selected departure date range.";
        } else if (hasSearch && hasAfter) {
            noTripsMessage =
                "No trips match your search with departures on or after the selected date.";
        } else if (hasSearch && hasBefore) {
            noTripsMessage =
                "No trips match your search with departures on or before the selected date.";
        } else if (hasAfter && hasBefore) {
            noTripsMessage =
                "No trips depart within the selected date range.";
        } else if (hasSearch) {
            noTripsMessage =
                "No trips match your search.";
        } else if (hasAfter) {
            noTripsMessage =
                "No trips depart on or after the selected date.";
        } else if (hasBefore) {
            noTripsMessage =
                "No trips depart on or before the selected date.";
        }
        return noTripsMessage;
    }

    // Date string formatting

    const formatDateParts = (dateStr) => {
        if (!dateStr) return { date: "—", day: "" };
        const d = new Date(dateStr);
        return {
            date: d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }),
            day: d.toLocaleDateString("en-IN", { weekday: "long" }),
        };
    };

    // Initializing carousel reference

    const carouselRef = useRef(null);
    const scrollCarousel = (direction) => {
        if (carouselRef.current) {
            carouselRef.current.scrollBy({ left: direction * 280, behavior: "smooth" });
        }
    };

    // Clear all filter states

    const onClearFilters = () => {
        onSearchDepartureChange("");
        onTravelAfterDateChange(null);
        onTravelBeforeDateChange(null);
    }

    // Rendering Component

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>

            {(isLoadingTripPackages || isLoadingOrderForEdit) ? (
                <SelectTripStepSkeleton/>
            ): (
                <MDBox>
                    <SectionCard title="Select Trip Package" id="select-trip-section">

                        {/* Filter header */}

                        <MDBox display="flex" flexWrap="wrap" gap={1} mb={2} alignItems="center">

                            {/* FILTER BY : Trip Destination, Package Title */}

                            <MDBox flex={1} minWidth={180}>
                                <MDTypography variant="caption" fontWeight="bold" color="text" mb={0.5} display="block">
                                    Search By Destination
                                </MDTypography>
                                <Autocomplete
                                    freeSolo
                                    // CHECK THIS : NEED TO GET ONLY UNIQUE ONES _ NO REPEATS PLEASE
                                    options={[...new Set(filteredTripPackages.flatMap((t) => t.destinations))]}
                                    value={searchDeparture}
                                    onChange={(_, val) => onSearchDepartureChange(val || "")}
                                    onInputChange={(_, val) => onSearchDepartureChange(val)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="Search trips by name or departure cities..."
                                            size="small"
                                            fullWidth
                                        />
                                    )}
                                />
                                <MDTypography
                                    variant="caption"
                                    color="info"
                                    display="block"
                                    mt={0.5}
                                    sx={{ visibility: "hidden" }}
                                >
                                    Filter
                                </MDTypography>
                            </MDBox>

                            {/* FILTER BY : Departure Comes After */}

                            <MDBox flex={1} minWidth={180}>
                                <MDTypography variant="caption" fontWeight="bold" color="text" mb={0.5} display="block">
                                    Departure Date From
                                </MDTypography>
                                <DatePicker
                                    value={travelAfterDate}
                                    onChange={onTravelAfterDateChange}
                                    slotProps={{ textField: { size: "small", fullWidth: true } }}
                                    error={Boolean(fieldErrors.travelAfterDate)}
                                />
                                <MDTypography
                                    variant="caption"
                                    color="error"
                                    display="block"
                                    mt={0.5}
                                    sx={{ visibility: Boolean(fieldErrors.travelAfterDate) ? "visible" : "hidden" }}
                                >
                                    {/* The period at the end is must, making it take the space while being hidden */}
                                    {fieldErrors.travelAfterDate}.
                                </MDTypography>
                            </MDBox>

                            {/* FILTER BY : Departure Comes Before */}

                            <MDBox flex={1} minWidth={180}>
                                <MDTypography variant="caption" fontWeight="bold" color="text" mb={0.5} display="block">
                                    Departure Date To
                                </MDTypography>
                                <DatePicker
                                    value={travelBeforeDate}
                                    onChange={onTravelBeforeDateChange}
                                    slotProps={{ textField: { size: "small", fullWidth: true } }}
                                    error={Boolean(fieldErrors.travelBeforeDate)}
                                />
                                <MDTypography
                                    variant="caption"
                                    color="error"
                                    display="block"
                                    mt={0.5}
                                    sx={{ visibility: Boolean(fieldErrors.travelBeforeDate) ? "visible" : "hidden" }}
                                >
                                    {/* The period at the end is must, making it take the space while being hidden */}
                                    {fieldErrors.travelBeforeDate}.
                                </MDTypography>
                            </MDBox>

                            {/* CLEAR FILTERS BUTTON */}

                            <MDBox>
                                <Tooltip title="Clear filter">
                                    <IconButton
                                        onClick={onClearFilters}
                                        border="sm"
                                        sx={{
                                            height: 45,
                                            width: 45,
                                            bgcolor: "white !important",
                                            padding: 1,
                                            borderRadius: 1,
                                            border: "1px solid #ddd",
                                            "&:hover": { bgcolor: "grey.100" },
                                        }}
                                        size="small"
                                    >
                                        <FilterAltOffIcon />
                                    </IconButton>
                                </Tooltip>
                            </MDBox>

                        </MDBox>

                        {/* Package Selection Carousel */}

                        {
                            filteredTripPackages.length < 1 ?

                                // If there is no trip packages available after filters

                                <MDBox display="flex" mt={9} mb={9.5} alignItems="center" justifyContent="center">
                                    <MDTypography
                                        variant="caption"
                                        fontWeight="bold"
                                        color="text"
                                        display="block"
                                    >
                                        {getNoTripPackageMessage()}
                                    </MDTypography>
                                </MDBox> :

                                // Rendering trips carousel

                                <MDBox position="relative" px={3}>

                                    {/* Carousel Left Button */}

                                    {filteredTripPackages.length > 3 && (
                                        <IconButton
                                            onClick={() => scrollCarousel(-1)}
                                            sx={{
                                                position: "absolute",
                                                left: -8,
                                                top: "50%",
                                                transform: "translateY(-50%)",
                                                zIndex: 2,
                                                bgcolor: "white !important",
                                                boxShadow: 2,
                                                "&:hover": { bgcolor: "grey.100" },
                                            }}
                                            size="small"
                                        >
                                            <Icon>chevron_left</Icon>
                                        </IconButton>
                                    )}

                                    {/* Trip selection menu */}

                                    <MDBox
                                        ref={carouselRef}
                                        display="flex"
                                        gap={2}
                                        sx={{ overflowX: "auto", scrollBehavior: "smooth", py: 1, px: 0.5, "&::-webkit-scrollbar": { display: "none" } }}
                                    >
                                        {filteredTripPackages.map((tripPackage) => {
                                            const isSelected = selectedTripPackage?.id === tripPackage.id;
                                            return (
                                                <MDBox
                                                    key={tripPackage.id}
                                                    onClick={() => onSelectTripPackage(tripPackage)}
                                                    sx={{
                                                        minWidth: 240,
                                                        maxWidth: 240,
                                                        borderRadius: 2,
                                                        overflow: "hidden",
                                                        cursor: "pointer",
                                                        border: isSelected ? "3px solid #1565C8" : "1px solid #E0E6EF",
                                                        boxShadow: isSelected ? "0 4px 16px rgba(21,101,200,0.2)" : "0 2px 8px rgba(14,35,72,0.06)",
                                                        transition: "all 0.2s ease",
                                                        position: "relative",
                                                        flexShrink: 0,
                                                        bgcolor: "white",
                                                    }}
                                                >
                                                    {isSelected && (
                                                        <CheckCircleIcon
                                                            sx={{
                                                                position: "absolute",
                                                                top: 10,
                                                                right: 10,
                                                                color: "#2E7D32",
                                                                bgcolor: "#FFFFFF",
                                                                borderRadius: "50%",
                                                                fontSize: 28,
                                                                zIndex: 1,
                                                                boxShadow: "0 2px 6px rgba(14,35,72,0.25)",
                                                            }}
                                                        />
                                                    )}
                                                    <MDBox
                                                        component="img"
                                                        src={tripPackage.image || "/assets/images/trip-placeholder.png"}
                                                        alt={tripPackage.title}
                                                        sx={{ width: "100%", height: 130, objectFit: "cover" }}
                                                    />
                                                    <MDBox p={1.5}>
                                                        <MDTypography variant="button" fontWeight="bold" display="block" noWrap>
                                                            {tripPackage.title}
                                                        </MDTypography>
                                                        <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                                                            {tripPackage.duration}
                                                        </MDTypography>
                                                        <MDBox display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                                                            <MDTypography variant="button" color="info" fontWeight="bold">
                                                                {formatAmount(tripPackage.base_price, tripPackage.currency)}
                                                                <MDTypography component="span" variant="caption" color="text">
                                                                    {" "}/ person
                                                                </MDTypography>
                                                            </MDTypography>
                                                            <MDBox display="flex" alignItems="center" gap={0.25}>
                                                                <StarIcon sx={{ fontSize: 14, color: "#E68A06" }} />
                                                                <MDTypography variant="caption" fontWeight="bold">
                                                                    {tripPackage.rating || '5'}
                                                                </MDTypography>
                                                                <MDTypography variant="caption" color="text">
                                                                    ({tripPackage.reviews || '21'})
                                                                </MDTypography>
                                                            </MDBox>
                                                        </MDBox>
                                                    </MDBox>
                                                </MDBox>
                                            );
                                        })}
                                    </MDBox>

                                    {/* Right Carousel Button */}

                                    {filteredTripPackages.length > 3 && (
                                        <IconButton
                                            onClick={() => scrollCarousel(1)}
                                            sx={{
                                                position: "absolute",
                                                right: -8,
                                                top: "50%",
                                                transform: "translateY(-50%)",
                                                zIndex: 2,
                                                bgcolor: "white !important",
                                                boxShadow: 2,
                                                "&:hover": { bgcolor: "grey.100" },
                                            }}
                                            size="small"
                                        >
                                            <Icon>chevron_right</Icon>
                                        </IconButton>
                                    )}
                                </MDBox>
                        }

                        {/* Trip Error */}

                        {fieldErrors.trip && (
                            <Alert severity="warning" sx={{ mt: 1.5 }}>
                                {fieldErrors.trip}
                            </Alert>
                        )}

                    </SectionCard>

                    {/* Selected Trip Summary */}

                    {selectedTripPackage && (
                        <SectionCard title="Selected Trip Package Details">
                            <MDBox
                                display="flex"
                                flexWrap="wrap"
                                gap={2}
                                alignItems="center"
                                justifyContent="space-between"
                                sx={{ bgcolor: "#F4F8FD", borderRadius: 2, p: 2 }}
                            >
                                <MDBox display="flex" gap={2} alignItems="center" flex={1}>
                                    <MDBox
                                        component="img"
                                        src={selectedTripPackage.image}
                                        alt={selectedTripPackage.title}
                                        sx={{ width: 100, height: 72, borderRadius: 1.5, objectFit: "cover" }}
                                    />
                                    <MDBox>
                                        <MDTypography variant="h6" fontWeight="bold">
                                            {selectedTripPackage.title}
                                        </MDTypography>
                                        <MDTypography sx={{textTransform: "capitalize"}} variant="caption" color="text" display="block">
                                            {selectedTripPackage.duration}
                                        </MDTypography>
                                    </MDBox>
                                </MDBox>

                                <MDBox display="flex" gap={10} flexWrap="wrap" alignItems="center">
                                    <DetailChip label="Base Price" value={`${formatAmount(selectedTripPackage.base_price, selectedTripPackage.currency)} / person`} />
                                </MDBox>

                                <MDButton variant="outlined" color="info" size="small" onClick={ClearPackageSelection}>
                                    Change Package
                                </MDButton>
                            </MDBox>
                        </SectionCard>
                    )}

                    {/* Departure Date Select Menu */}

                    {selectedTripPackage && (() => {
                        const validDepartureDates = selectedTripPackage
                            ?.departure_dates
                            ?.filter((d) => isNaN(travelAfterDate) || new Date(d.departure_date) >= travelAfterDate)
                            ?.filter((d) => isNaN(travelBeforeDate) || new Date(d.departure_date) >= travelBeforeDate)
                            // ?.filter((d) => new Date(d.book_by_date).setHours(0, 0, 0, 0) >= new Date().setHours(0, 0, 0, 0))
                        ;
                        return (
                            <SectionCard title={`Available Departures (${validDepartureDates?.length ?? 0})`}
                                         id="departure-date-section">
                                <MDTypography variant="caption" color="text" display="block" mb={1.5}>
                                    Select your preferred departure date
                                </MDTypography>

                                {(validDepartureDates?.length ?? 0) === 0 ? (
                                    <MDTypography variant="caption" color="text" sx={{p: 2, display: "block"}}>
                                        No departure dates configured yet.
                                    </MDTypography>
                                ) : (
                                    <MDBox>
                                        {/* Header row */}
                                        <MDBox
                                            display="grid"
                                            sx={{
                                                gridTemplateColumns: "48px 1.3fr 1.3fr 1.3fr 0.8fr 1.2fr",
                                                alignItems: "center",
                                                bgcolor: "#F4F6F9",
                                                borderRadius: "5px",
                                                px: 2,
                                                py: 1,
                                                mb: 1,
                                            }}
                                        >
                                            <MDTypography variant="caption" fontWeight="bold" color="text"/>
                                            <MDTypography variant="caption" fontWeight="bold" color="text">Departure
                                                Date</MDTypography>
                                            <MDTypography variant="caption" fontWeight="bold" color="text">Return
                                                Date</MDTypography>
                                            <MDTypography variant="caption" fontWeight="bold" color="text">Price (per
                                                person)</MDTypography>
                                            <MDTypography variant="caption" fontWeight="bold" color="text">Seats
                                                Left</MDTypography>
                                            <MDTypography variant="caption" fontWeight="bold" color="text" textAlign="center">Status
                                                </MDTypography>
                                        </MDBox>

                                        {/* Data rows */}
                                        <MDBox display="flex" flexDirection="column" gap={1}>
                                            {validDepartureDates?.map((template, index) => {
                                                const bookable = new Date(template.book_by_date).setHours(0, 0, 0, 0) >= new Date().setHours(0, 0, 0, 0)
                                                const rowId = template.id ?? index;
                                                const isSelected = selectedDepartureDate?.id === rowId;
                                                const dep =      formatDateParts(template.departure_date);
                                                const ret = formatDateParts(template.return_date);
                                                const secured = allocationMap[rowId] ?? 0;
                                                const seatsLeft = template.total_allocation != null
                                                    ? Math.max(template.total_allocation - secured, 0)
                                                    : null;

                                                return (
                                                    <MDBox
                                                        key={rowId}
                                                        onClick={() => bookable && onChangeDepartureDate(template)}
                                                        display="grid"
                                                        sx={{
                                                            gridTemplateColumns: "48px 1.3fr 1.3fr 1.3fr 0.8fr 1.2fr",
                                                            alignItems: "center",
                                                            border: isSelected ? "2px solid #1565C8" : "1px solid #E8EDF5",
                                                            borderRadius: "5px",
                                                            px: 2,
                                                            py: 1.25,
                                                            cursor: bookable ? "pointer" : "not-allowed",
                                                            bgcolor: !bookable ? "#F5F5F5" : isSelected ? "#F5F9FF" : "white",
                                                            opacity: bookable ? 1 : 0.6,
                                                            transition: "border-color 0.15s ease, background-color 0.15s ease",
                                                            "&:hover": bookable ? { borderColor: "#1565C8" } : {},
                                                        }}
                                                    >
                                                        <Radio
                                                            size="small"
                                                            checked={isSelected}
                                                            onClick={(e) => e.stopPropagation()}
                                                            onChange={() => bookable && onChangeDepartureDate(template)}
                                                            sx={{
                                                                cursor: bookable ? "pointer" : "not-allowed",
                                                            }}
                                                        />

                                                        <MDBox>
                                                            <MDTypography variant="button" fontWeight="medium"
                                                                          display="block">
                                                                {dep.date}
                                                            </MDTypography>
                                                            <MDTypography variant="caption" color="text">
                                                                {dep.day}
                                                            </MDTypography>
                                                        </MDBox>

                                                        <MDBox>
                                                            <MDTypography variant="button" fontWeight="medium"
                                                                          display="block">
                                                                {ret.date}
                                                            </MDTypography>
                                                            <MDTypography variant="caption" color="text">
                                                                {ret.day}
                                                            </MDTypography>
                                                        </MDBox>
                                                        {(() => {
                                                            const basePrice = selectedTripPackage.base_price;
                                                            const discountPercentage = template?.discount_percentage || 0;
                                                            const discountedPrice = basePrice - (basePrice * discountPercentage) / 100;

                                                            // Resolve the offer window once per row so we never render a
                                                            // discount as "live" outside its actual claim window.
                                                            const offerWindow = getOfferWindow(template);
                                                            const offerStyle = offerWindow
                                                                ? (OFFER_TAG_STYLES[offerWindow.type] || OFFER_TAG_STYLES.group_deal)
                                                                : null;

                                                            const isLive = discountPercentage > 0 && (!offerWindow || offerWindow.isActive);
                                                            const isEnded = discountPercentage > 0 && offerWindow?.isExpired;

                                                            // --- HSS surcharge ---
                                                            const hssTemplate = template.hss_template ? hssTemplateMap[template.hss_template] : null;
                                                            const hssWindow = getHSSWindow(hssTemplate);
                                                            const isHssLive = Boolean(hssTemplate) && Boolean(hssWindow?.isActive);
                                                            const hssSurcharge = isHssLive ? calculateHssSurcharge(basePrice, hssTemplate, template) : 0;

                                                            const finalPrice = (isLive ? discountedPrice : basePrice) + hssSurcharge;

                                                            return (
                                                                <MDBox display="flex" flexDirection="column" gap={0.25}>
                                                                    {isLive && (
                                                                        <MDBox display="flex" gap={0.5} flexWrap="wrap"
                                                                               alignItems="center">
                                                                            <MDBox
                                                                                px={0.5}
                                                                                py={0.1}
                                                                                sx={{
                                                                                    display: "inline-flex",
                                                                                    alignItems: "center",
                                                                                    bgcolor: "#E8F5E9",
                                                                                    color: "#2E7D32",
                                                                                    borderRadius: "4px",
                                                                                    fontSize: "0.65rem",
                                                                                    fontWeight: 600,
                                                                                    width: "fit-content",
                                                                                }}
                                                                            >
                                                                                {discountPercentage}% OFF
                                                                            </MDBox>
                                                                            {offerStyle && (
                                                                                <MDBox
                                                                                    px={0.5}
                                                                                    py={0.1}
                                                                                    sx={{
                                                                                        display: "inline-flex",
                                                                                        alignItems: "center",
                                                                                        gap: 0.25,
                                                                                        bgcolor: offerStyle.bg,
                                                                                        color: offerStyle.color,
                                                                                        borderRadius: "4px",
                                                                                        fontSize: "0.65rem",
                                                                                        fontWeight: 600,
                                                                                        width: "fit-content",
                                                                                    }}
                                                                                >
                                                                                    <Icon
                                                                                        sx={{fontSize: "12px !important"}}>{offerStyle.icon}</Icon>
                                                                                    {offerStyle.label}
                                                                                </MDBox>
                                                                            )}
                                                                        </MDBox>
                                                                    )}

                                                                    {isEnded && offerStyle && (
                                                                        <MDBox
                                                                            px={0.5}
                                                                            py={0.1}
                                                                            sx={{
                                                                                display: "inline-flex",
                                                                                alignItems: "center",
                                                                                gap: 0.25,
                                                                                bgcolor: "#EEEEEE",
                                                                                color: "#9E9E9E",
                                                                                borderRadius: "4px",
                                                                                fontSize: "0.65rem",
                                                                                fontWeight: 600,
                                                                                width: "fit-content",
                                                                            }}
                                                                        >
                                                                            <Icon
                                                                                sx={{fontSize: "12px !important"}}>schedule</Icon>
                                                                            {offerStyle.label} ended
                                                                        </MDBox>
                                                                    )}

                                                                    {/* NEW: HSS surcharge tag */}
                                                                    {isHssLive && (
                                                                        <MDBox
                                                                            px={0.5}
                                                                            py={0.1}
                                                                            sx={{
                                                                                display: "inline-flex",
                                                                                alignItems: "center",
                                                                                gap: 0.25,
                                                                                bgcolor: HSS_TAG_STYLE.bg,
                                                                                color: HSS_TAG_STYLE.color,
                                                                                borderRadius: "4px",
                                                                                fontSize: "0.65rem",
                                                                                fontWeight: 600,
                                                                                width: "fit-content",
                                                                            }}
                                                                        >
                                                                            <Icon
                                                                                sx={{fontSize: "12px !important"}}>{HSS_TAG_STYLE.icon}</Icon>
                                                                            +{formatAmount(hssSurcharge, selectedTripPackage.currency)} Surcharge
                                                                        </MDBox>
                                                                    )}

                                                                    <MDTypography variant="button" fontWeight="bold"
                                                                                  color="info">
                                                                        {formatAmount(finalPrice, selectedTripPackage.currency)}
                                                                    </MDTypography>

                                                                    {(isLive || isHssLive) && (
                                                                        <MDBox display="flex" alignItems="center"
                                                                               gap={0.25}>
                                                                            <MDTypography
                                                                                sx={{textDecoration: "line-through"}}
                                                                                variant="caption" color="text">
                                                                                {formatAmount(basePrice, selectedTripPackage.currency)}
                                                                            </MDTypography>
                                                                            {(offerWindow?.end || hssWindow?.end) && (
                                                                                <Tooltip
                                                                                    title={
                                                                                        [
                                                                                            offerWindow?.end ? `Discount valid until ${formatDateParts(offerWindow.end).date}` : null,
                                                                                            isHssLive ? `High season surcharge applies until ${formatDateParts(hssWindow.end).date}` : null,
                                                                                            template.book_by_date ? `Booking closes ${formatDateParts(template.book_by_date).date}` : null,
                                                                                        ].filter(Boolean).join(" · ")
                                                                                    }
                                                                                    arrow
                                                                                    placement="top"
                                                                                >
                                                                                    <InfoOutlinedIcon sx={{
                                                                                        fontSize: 14,
                                                                                        ml: 0.5,
                                                                                        color: 'black',
                                                                                        cursor: 'help',
                                                                                        verticalAlign: 'middle'
                                                                                    }}/>
                                                                                </Tooltip>
                                                                            )}
                                                                        </MDBox>
                                                                    )}
                                                                </MDBox>
                                                            )
                                                        })()}

                                                        {
                                                            isLoadingAllocations ?
                                                                <Skeleton variant="text" width="40%" height={20}/> :
                                                                <MDTypography
                                                                    variant="caption"
                                                                    fontWeight="medium"
                                                                    color={seatsLeft != null && seatsLeft <= 3 ? "error" : "text"}
                                                                >
                                                                    {seatsLeft != null ? `${seatsLeft} Seats` : "-"}
                                                                </MDTypography>
                                                        }

                                                        <Chip
                                                            size="small"
                                                            label={bookable ? template.status.replace('_', ' ') : "Booking closed"}
                                                            sx={{
                                                                textTransform: "capitalize",
                                                                bgcolor: "#F0F0F0",
                                                                color: "#616161",
                                                                fontWeight: 600,
                                                                fontSize: "0.7rem",
                                                                width: "fit-content",
                                                                mx: "auto",
                                                            }}
                                                        />
                                                    </MDBox>
                                                );
                                            })}
                                        </MDBox>

                                        {/* Departure date error */}

                                        {fieldErrors.departureDate && (
                                            <Alert severity="warning" sx={{mt: 1.5}}>
                                                {fieldErrors.departureDate}
                                            </Alert>
                                        )}

                                    </MDBox>
                                )}
                            </SectionCard>
                        )
                    })()}

                    {/* Selected Departure Date Summary */}

                    {selectedDepartureDate != null && (() => {
                        const dep = formatDateParts(selectedDepartureDate.departure_date);
                        const ret = formatDateParts(selectedDepartureDate.return_date);

                        return (
                            <MDBox mb={3} p={3} borderRadius="lg" sx={{ bgcolor: "#F0F7EF", border: "1px solid #CFE8CC" }}>

                                {/* Selected Departure Date Info */}

                                <MDBox display="flex" alignItems="center" gap={1} mb={2.5}>
                                    <Icon sx={{ color: "#2E7D32", fontSize: 22 }}>check_circle</Icon>
                                    <MDTypography variant="button" fontWeight="bold" color="dark" sx={{ fontSize: "0.95rem" }}>
                                        Selected Departure
                                    </MDTypography>
                                </MDBox>

                                <MDBox
                                    display="grid"
                                    sx={{
                                        gridTemplateColumns: {
                                            xs: "repeat(2, 1fr)",
                                            sm: "repeat(4, 1fr)",
                                        },
                                        columnGap: 4,
                                        rowGap: 2.5,
                                    }}
                                >
                                    <SummaryItem icon="flight_takeoff" label="Departure" value={dep.date} sub={dep.day} />
                                    <SummaryItem icon="flight_land" label="Return" value={ret.date} sub={ret.day} />
                                    <SummaryItem
                                        icon="schedule"
                                        label="Duration"
                                        value={selectedTripPackage?.duration}
                                    />
                                    <SummaryItem icon="location_on" label="Departure City" value={selectedDepartureDate.departure_city || "Any"} />
                                </MDBox>
                            </MDBox>
                        );
                    })()}

                    {/* Departure Date Inclusion Summary */}

                    {selectedTripPackage && (
                        <SectionCard title="Trip Inclusions">
                            <MDTypography variant="body2" color="text" sx={{ lineHeight: 1.8 }}>
                                {selectedTripPackage?.inclusions ? (
                                        <MDBox px={2}
                                            dangerouslySetInnerHTML={{
                                                __html: DOMPurify.sanitize(selectedTripPackage?.inclusions || ""),
                                            }}
                                        />
                                ) : (
                                    "Inclusion is not configured for this trip"
                                )}
                            </MDTypography>
                        </SectionCard>
                    )}

                    {/* Info banner */}

                    <MDBox
                        mt={2}
                        p={2}
                        borderRadius="lg"
                        sx={{ bgcolor: "#E3F2FD", border: "1px solid #BBDEFB" }}
                        display="flex"
                        alignItems="center"
                        gap={1}
                    >
                        <Icon color="info">info</Icon>
                        <MDTypography variant="caption" color="info" fontWeight="medium">
                            You can add travelers and customize your trip in the next steps.
                        </MDTypography>
                    </MDBox>
                </MDBox>
            )}
        </LocalizationProvider>
    );
}

function SectionCard({ title, children, id }) {
    return (
        <MDBox id={id} mb={3}>
            <MDTypography variant="h6" fontWeight="bold" mb={1.5}>
                {title}
            </MDTypography>
            <MDBox sx={{ bgcolor: "white", borderRadius: 2, p: 2.5, border: "1px solid #E8EDF5" }}>
                {children}
            </MDBox>
        </MDBox>
    );
}

function DetailChip({ label, value, highlight }) {
    return (
        <MDBox textAlign="center">
            <MDTypography variant="caption" color="text" display="block">
                {label}
            </MDTypography>
            <MDTypography variant="button" fontWeight="bold" color={highlight ? "info" : "dark"}>
                {value}
            </MDTypography>
        </MDBox>
    );
}

function SummaryItem({ icon, label, value, sub, valueColor, size = 1 }) {
    const span = Math.min(4, Math.max(1, size));
    return (
        <MDBox
            display="flex"
            gap={1.25}
            alignItems="flex-start"
            sx={{ gridColumn: { xs: "span 2", sm: `span ${span}` } }}
        >
            <MDBox
                sx={{
                    width: 30,
                    height: 30,
                    borderRadius: "50%",
                    bgcolor: "rgba(46,125,50,0.12)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                }}
            >
                <Icon sx={{ color: "#2E7D32", fontSize: 17 }}>{icon}</Icon>
            </MDBox>
            <MDBox>
                <MDTypography variant="caption" color="text" display="block" sx={{ mb: 0.25 }}>
                    {label}
                </MDTypography>
                <MDTypography
                    variant="button"
                    fontWeight="bold"
                    display="block"
                    sx={{ color: valueColor || "#1A2035", lineHeight: 1.3 }}
                >
                    {value}
                </MDTypography>
                {sub && (
                    <MDTypography variant="caption" color="text" sx={{ lineHeight: 1.3 }}>
                        {sub}
                    </MDTypography>
                )}
            </MDBox>
        </MDBox>
    );
}

SectionCard.propTypes = {
    title: PropTypes.string.isRequired,
    children: PropTypes.node,
    id: PropTypes.string,
};

DetailChip.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    highlight: PropTypes.bool,
};

SummaryItem.propTypes = {
    icon: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.node.isRequired,
    sub: PropTypes.string,
    valueColor: PropTypes.string,
    size: PropTypes.oneOf([1, 2, 3, 4]),
};

export default SelectTripStep;